import React, { useCallback } from "react";
import {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  FieldErrors,
} from "react-hook-form";
import { MapPin } from "lucide-react";
import { FormField, Input, Select } from "../UI/FormField";
import { SectionCard } from "../UI/SectionCard";
import { CreateShipmentData, Country, EntitySnapshot } from "../../types";
import { PartnerAutocomplete } from "../shipments/PartnerAutoComplete";
import { ToggleSwitch } from "../shipments/ToggleSwitch";
import { AddressAutocompleteField } from "../GooglePlaces/AddressAutocompleteField";
import { useTranslation } from "react-i18next";

interface PickupDeliverySectionProps {
  register: UseFormRegister<CreateShipmentData>;
  setValue: UseFormSetValue<CreateShipmentData>;
  watch: UseFormWatch<CreateShipmentData>;
  errors: FieldErrors<CreateShipmentData>;
  readonly: boolean;
  getErrorMessage: (error: any) => string | undefined;
  countries: Country[];
  countryOptions: Array<{ value: string; label: string }>;
  apiKey: string;
  clientSyncMode: "none" | "pickup" | "delivery";
  setClientSyncMode: (mode: "none" | "pickup" | "delivery") => void;
  /*
   * DEAD PROPS — kept commented for reference. Wired the legacy modal-based
   * Add/Edit flow. Replaced by inline partner fields in this component +
   * PartnerAutocomplete's built-in clear (X).
   *
   *   pickupSnapshot?: EntitySnapshot;
   *   deliverySnapshot?: EntitySnapshot;
   *   onPickupSnapshotSelect: (snapshot: EntitySnapshot) => void;
   *   onDeliverySnapshotSelect: (snapshot: EntitySnapshot) => void;
   *   onPickupAdd: () => void;
   *   onDeliveryAdd: () => void;
   *   onPickupEdit: () => void;
   *   onDeliveryEdit: () => void;
   *   onPickupClear: () => void;
   *   onDeliveryClear: () => void;
   */
}

type Side = "pickup" | "delivery";

export const PickupDeliverySection: React.FC<PickupDeliverySectionProps> = ({
  register,
  setValue,
  watch,
  errors,
  readonly,
  getErrorMessage,
  clientSyncMode,
  setClientSyncMode,
  apiKey,
  countries,
  countryOptions,
}) => {
  const { t } = useTranslation("shipments");

  const watchPickupCompanyName = watch("pickup.companyName");
  const watchDeliveryCompanyName = watch("delivery.companyName");
  const watchPickupStreet = watch("pickup.address.street");
  const watchDeliveryStreet = watch("delivery.address.street");

  const handleToggleClientSync = useCallback(
    (mode: Side) => {
      if (mode === "pickup") {
        if (clientSyncMode === "pickup") {
          setClientSyncMode("none");
          setValue("clientSync.clientSameAsPickup", false, { shouldValidate: true });
        } else {
          setClientSyncMode("pickup");
          setValue("clientSync.clientSameAsPickup", true, { shouldValidate: true });
          setValue("clientSync.clientSameAsDelivery", false, { shouldValidate: true });
        }
      } else {
        if (clientSyncMode === "delivery") {
          setClientSyncMode("none");
          setValue("clientSync.clientSameAsDelivery", false, { shouldValidate: true });
        } else {
          setClientSyncMode("delivery");
          setValue("clientSync.clientSameAsDelivery", true, { shouldValidate: true });
          setValue("clientSync.clientSameAsPickup", false, { shouldValidate: true });
        }
      }
    },
    [clientSyncMode, setClientSyncMode, setValue],
  );

  /**
   * Apply an EntitySnapshot from the partner autocomplete to all inline
   * fields of the given side. We do NOT track partnerId — the snapshot is
   * just used to seed the inline fields, and the user can edit freely after.
   */
  const applySnapshot = useCallback(
    (snapshot: EntitySnapshot, prefix: Side) => {
      const country = snapshot?.address?.country;
      setValue(`${prefix}.companyName`, snapshot.companyName);
      setValue(`${prefix}.registerationCode`, snapshot.registerationCode || "");
      setValue(`${prefix}.phoneNumber`, snapshot.phoneNumber || "");
      setValue(`${prefix}.emailAddress`, snapshot.emailAddress || "");
      setValue(`${prefix}.address.street`, snapshot.address.street);
      setValue(`${prefix}.address.city`, snapshot.address.city);
      setValue(`${prefix}.address.state`, snapshot.address.state);
      setValue(`${prefix}.address.postalCode`, snapshot.address.postalCode || "");
      setValue(
        `${prefix}.address.country`,
        typeof country === "object" && country !== null ? country._id : country,
      );
    },
    [setValue],
  );

  const clearSide = useCallback(
    (prefix: Side) => {
      setValue(`${prefix}.companyName`, "");
      setValue(`${prefix}.registerationCode`, "");
      setValue(`${prefix}.phoneNumber`, "");
      setValue(`${prefix}.emailAddress`, "");
      setValue(`${prefix}.address.street`, "");
      setValue(`${prefix}.address.city`, "");
      setValue(`${prefix}.address.state`, "");
      setValue(`${prefix}.address.postalCode`, "");
      setValue(`${prefix}.address.country`, "");
    },
    [setValue],
  );

  const handleAddressSelect = useCallback(
    (addressData: any, prefix: Side) => {
      setValue(`${prefix}.address.street`, addressData.street);
      setValue(`${prefix}.address.city`, addressData.city);
      setValue(`${prefix}.address.state`, addressData.state);
      setValue(`${prefix}.address.postalCode`, addressData.postalCode);
      setValue(`${prefix}.address.country`, addressData.country);
    },
    [setValue],
  );

  const isPickupDisabled = clientSyncMode === "pickup";
  const isDeliveryDisabled = clientSyncMode === "delivery";

  const renderSide = (side: Side) => {
    const isPickup = side === "pickup";
    const titleKey = isPickup ? "pickup.title" : "deliverySection.title";
    const syncLabelKey = isPickup
      ? "pickup.sameAsClient"
      : "deliverySection.sameAsClient";
    const placeholderCompanyKey = isPickup
      ? "pickup.companyPlaceholder"
      : "deliverySection.companyPlaceholder";
    const placeholderAddressKey = isPickup
      ? "pickup.addressPlaceholder"
      : "deliverySection.addressPlaceholder";

    const sideErrors = isPickup ? errors.pickup : errors.delivery;
    const watchCompany = isPickup ? watchPickupCompanyName : watchDeliveryCompanyName;
    const watchStreet = isPickup ? watchPickupStreet : watchDeliveryStreet;
    const disabled = readonly || (isPickup ? isPickupDisabled : isDeliveryDisabled);
    const syncMode = isPickup ? "pickup" : ("delivery" as Side);
    const isSyncOn = clientSyncMode === syncMode;
    const iconColor = isPickup ? "text-green-600" : "text-red-600";

    return (
      <SectionCard
        icon={MapPin}
        iconClassName={iconColor}
        title={t(titleKey)}
        actions={
          <ToggleSwitch
            checked={isSyncOn}
            onChange={() => handleToggleClientSync(side)}
            label={t(syncLabelKey)}
          />
        }
      >
        <div className="space-y-1">
          <FormField
            layout="inline"
            size="sm"
            label={t("labels.date")}
            error={getErrorMessage(sideErrors?.date)}
          >
            <Input
              {...register(`${side}.date` as const)}
              type="date"
              size="sm"
              error={!!sideErrors?.date}
              readOnly={readonly}
            />
          </FormField>
          <FormField
            layout="inline"
            size="sm"
            label={t("labels.time")}
            error={getErrorMessage(sideErrors?.time)}
          >
            <Input
              {...register(`${side}.time` as const)}
              type="time"
              size="sm"
              error={!!sideErrors?.time}
              readOnly={readonly}
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("labels.company")}
            error={getErrorMessage(sideErrors?.companyName)}
          >
            <PartnerAutocomplete
              value={watchCompany || ""}
              onChange={(value) =>
                setValue(`${side}.companyName` as const, value)
              }
              onSnapshotSelect={(s) => applySnapshot(s, side)}
              onClear={() => clearSide(side)}
              placeholder={t(placeholderCompanyKey)}
              disabled={disabled}
              error={!!sideErrors?.companyName}
              addresses={true}
              type={side}
              size="sm"
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("labels.regCode")}
            error={getErrorMessage(sideErrors?.registerationCode)}
          >
            <Input
              {...register(`${side}.registerationCode` as const)}
              size="sm"
              error={!!sideErrors?.registerationCode}
              placeholder={t("placeholders.regCode")}
              readOnly={disabled}
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("labels.phone")}
            error={getErrorMessage(sideErrors?.phoneNumber)}
          >
            <Input
              {...register(`${side}.phoneNumber` as const)}
              size="sm"
              error={!!sideErrors?.phoneNumber}
              placeholder={t("placeholders.phone")}
              readOnly={disabled}
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("labels.email")}
            error={getErrorMessage(sideErrors?.emailAddress)}
          >
            <Input
              {...register(`${side}.emailAddress` as const)}
              type="email"
              size="sm"
              error={!!sideErrors?.emailAddress}
              placeholder={t("placeholders.email")}
              readOnly={disabled}
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("labels.address")}
            error={getErrorMessage(sideErrors?.address?.street)}
          >
            <AddressAutocompleteField
              apiKey={apiKey}
              value={watchStreet || ""}
              onChange={(value) =>
                setValue(`${side}.address.street` as const, value)
              }
              onAddressSelect={(data) => handleAddressSelect(data, side)}
              placeholder={t(placeholderAddressKey)}
              disabled={disabled}
              error={!!sideErrors?.address?.street}
              countries={countries}
              size="sm"
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("labels.city")}
            error={getErrorMessage(sideErrors?.address?.city)}
          >
            <Input
              {...register(`${side}.address.city` as const)}
              size="sm"
              error={!!sideErrors?.address?.city}
              placeholder={t("placeholders.city")}
              readOnly={disabled}
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("labels.state")}
            error={getErrorMessage(sideErrors?.address?.state)}
          >
            <Input
              {...register(`${side}.address.state` as const)}
              size="sm"
              error={!!sideErrors?.address?.state}
              placeholder={t("placeholders.state")}
              readOnly={disabled}
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("labels.postalCode")}
            error={getErrorMessage(sideErrors?.address?.postalCode)}
          >
            <Input
              {...register(`${side}.address.postalCode` as const)}
              size="sm"
              error={!!sideErrors?.address?.postalCode}
              placeholder={t("placeholders.postalCode")}
              readOnly={disabled}
            />
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("labels.country")}
            error={getErrorMessage(sideErrors?.address?.country)}
          >
            <Select
              {...register(`${side}.address.country` as const)}
              size="sm"
              error={!!sideErrors?.address?.country}
              options={countryOptions}
              disabled={disabled}
              placeholder={t("placeholders.country")}
            />
          </FormField>
        </div>
      </SectionCard>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
      {renderSide("pickup")}
      {renderSide("delivery")}
    </div>
  );
};
